import React from 'react';
import styled from 'styled-components';
import { AlertTriangle, Trash2, Info } from 'lucide-react';
import Modal from './Modal';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  title: string;
  message: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  type?: 'warning' | 'danger' | 'info';
  loading?: boolean;
}

const MessageContainer = styled.div`
  text-align: center;
  padding: 20px 0;
`;

const Message = styled.p`
  margin: 0;
  color: var(--text-primary);
  font-size: 16px;
  line-height: 1.5;
`;

const IconContainer = styled.div<{ type: 'warning' | 'danger' | 'info' }>`
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;

  ${props => {
    switch (props.type) {
      case 'warning':
        return 'color: var(--warning-color);';
      case 'danger':
        return 'color: var(--error-color);';
      case 'info':
      default:
        return 'color: var(--primary-color);';
    }
  }}
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  
  @media (max-width: 767px) {
    flex-direction: column-reverse;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 12px 24px;
  border: none;
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 100px;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  ${props => {
    switch (props.variant) {
      case 'danger':
        return `
          background: var(--error-color);
          color: white;
          
          &:hover:not(:disabled) {
            background: #e01e4d;
          }
          
          &:focus {
            outline: none;
            box-shadow: 0 0 0 2px var(--error-light);
          }
        `;
      case 'primary':
        return `
          background: var(--primary-color);
          color: white;
          border: 1px solid var(--primary-color);

          &:hover:not(:disabled) {
            background: transparent;
            color: var(--primary-color);
          }

          &:focus {
            outline: none;
            box-shadow: 0 0 0 2px var(--primary-light);
          }
        `;
      case 'secondary':
      default:
        return `
          background: var(--surface-color);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          
          &:hover:not(:disabled) {
            background: var(--hover-background);
          }
          
          &:focus {
            outline: none;
            box-shadow: 0 0 0 2px var(--primary-light);
          }
        `;
    }
  }}
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  margin-left: 8px;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "אישור",
  cancelText = "ביטול",
  type = 'info',
  loading = false
}) => {
  const getIcon = () => {
    switch (type) {
      case 'warning':
        return <AlertTriangle size={48} strokeWidth={1.5} />;
      case 'danger':
        return <Trash2 size={48} strokeWidth={1.5} />;
      case 'info':
      default:
        return <Info size={48} strokeWidth={1.5} />;
    }
  };

  const getConfirmVariant = () => {
    switch (type) {
      case 'danger':
        return 'danger';
      case 'warning':
      case 'info':
      default:
        return 'primary';
    }
  };

  const handleConfirm = () => {
    if (!loading && onConfirm) {
      onConfirm();
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      width="400px"
      closable={!loading}
      footer={
        <ButtonContainer>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            {cancelText}
          </Button>
          {onConfirm && (
            <Button
              variant={getConfirmVariant()}
              onClick={handleConfirm}
              disabled={loading}
            >
              {confirmText}
              {loading && <LoadingSpinner />}
            </Button>
          )}
        </ButtonContainer>
      }
    >
      <MessageContainer>
        <IconContainer type={type}>
          {getIcon()}
        </IconContainer>
        <Message>{message}</Message>
      </MessageContainer>
    </Modal>
  );
};

export default ConfirmDialog;